import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import notificationsAPI from '../services/systemNotificationService';

const SystemNotificationsContext = createContext(null);

const safeJsonParse = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const SystemNotificationsProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refreshUnreadCount = useCallback(async () => {
    const data = await notificationsAPI.unreadCount();
    setUnreadCount(Number(data?.unread_count || 0));
    return data;
  }, []);

  const fetchNotifications = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const data = await notificationsAPI.list(params);
      const normalized = Array.isArray(data)
        ? data.map((n) => ({
            ...n,
            metadata: safeJsonParse(n?.notif_metadata),
          }))
        : [];
      setItems(normalized);
      return normalized;
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = useCallback(async (id) => {
    if (!id) return;
    await notificationsAPI.markRead(id, true);
    setItems((prev) => prev.map((n) => (String(n.id) === String(id) ? { ...n, is_read: true } : n)));
    refreshUnreadCount().catch(() => {});
  }, [refreshUnreadCount]);

  const markSelectedAsRead = useCallback(async (ids = []) => {
    const cleaned = (Array.isArray(ids) ? ids : []).filter(Boolean).map(String);
    if (!cleaned.length) return;
    await notificationsAPI.markSelectedRead(cleaned);
    setItems((prev) => prev.map((n) => (cleaned.includes(String(n.id)) ? { ...n, is_read: true } : n)));
    refreshUnreadCount().catch(() => {});
  }, [refreshUnreadCount]);

  const markAllAsRead = useCallback(async () => {
    await notificationsAPI.markAllRead();
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
  }, []);

  const removeNotification = useCallback(async (id) => {
    if (!id) return;
    await notificationsAPI.delete(id);
    setItems((prev) => prev.filter((n) => String(n.id) !== String(id)));
    refreshUnreadCount().catch(() => {});
  }, [refreshUnreadCount]);

  const handlePushNotification = useCallback((notification) => {
    if (!notification?.id) return;
    const enriched = {
      ...notification,
      metadata: safeJsonParse(notification?.notif_metadata),
    };
    setItems((prev) => {
      const exists = prev.some((n) => String(n.id) === String(enriched.id));
      if (exists) {
        return prev.map((n) => (String(n.id) === String(enriched.id) ? { ...n, ...enriched } : n));
      }
      return [enriched, ...prev].slice(0, 50);
    });
    if (notification?.is_read === false) {
      setUnreadCount((c) => c + 1);
    }
  }, []);

  const value = useMemo(() => ({
    unreadCount,
    items,
    loading,
    refreshUnreadCount,
    fetchNotifications,
    markAsRead,
    markSelectedAsRead,
    markAllAsRead,
    removeNotification,
    handlePushNotification,
  }), [
    unreadCount,
    items,
    loading,
    refreshUnreadCount,
    fetchNotifications,
    markAsRead,
    markSelectedAsRead,
    markAllAsRead,
    removeNotification,
    handlePushNotification,
  ]);

  return (
    <SystemNotificationsContext.Provider value={value}>
      {children}
    </SystemNotificationsContext.Provider>
  );
};

export const useSystemNotifications = () => {
  const ctx = useContext(SystemNotificationsContext);
  if (!ctx) throw new Error('useSystemNotifications must be used within SystemNotificationsProvider');
  return ctx;
};
